import * as THREE from "three";

export interface FireSite { key: string; x: number; y: number; until: number | null }
export interface IncomingSignal { x: number; y: number; count: number; arrivesAt: number }
export const MAX_FIRE_SITES = 16;
const FLAMES = 7, PUFFS = 4;

/** Local, bounded world VFX. The alarm marks the known target; it never invents an enemy route. */
export class WorldFx {
  readonly group = new THREE.Group();
  readonly alarm = new THREE.Group();
  private readonly flame: THREE.InstancedMesh;
  private readonly core: THREE.InstancedMesh;
  private readonly smoke: THREE.InstancedMesh;
  private readonly geometries: THREE.BufferGeometry[] = [];
  private readonly materials: THREE.Material[] = [];
  private readonly dummy = new THREE.Object3D();
  private sites: FireSite[] = [];
  private incoming: IncomingSignal | null = null;
  private disposed = false;

  constructor() {
    this.group.name = "world-fx";
    const flameGeo = new THREE.LatheGeometry([new THREE.Vector2(0, 0), new THREE.Vector2(0.64, 0.06), new THREE.Vector2(1, 0.25), new THREE.Vector2(0.7, 0.55), new THREE.Vector2(0.28, 0.82), new THREE.Vector2(0, 1)], 7);
    const tips = flameGeo.getAttribute("position");
    for (let i = 0; i < tips.count; i++) tips.setX(i, tips.getX(i) + tips.getY(i) ** 2 * 0.34);
    flameGeo.computeVertexNormals();
    const smokeGeo = new THREE.IcosahedronGeometry(1, 1);
    const flameMat = new THREE.MeshBasicMaterial({ color: 0xf48132, transparent: true, opacity: 0.84, depthWrite: false, toneMapped: false });
    const coreMat = new THREE.MeshBasicMaterial({ color: 0xffdf8b, transparent: true, opacity: 0.95, depthWrite: false, toneMapped: false });
    const smokeMat = new THREE.MeshBasicMaterial({ color: 0x5d655f, transparent: true, opacity: 0.34, depthWrite: false });
    for (const m of [flameMat, coreMat, smokeMat]) m.forceSinglePass = true;
    this.geometries.push(flameGeo, smokeGeo);
    this.materials.push(flameMat, coreMat, smokeMat);
    this.flame = new THREE.InstancedMesh(flameGeo, flameMat, MAX_FIRE_SITES * FLAMES);
    this.core = new THREE.InstancedMesh(flameGeo, coreMat, MAX_FIRE_SITES * FLAMES);
    this.smoke = new THREE.InstancedMesh(smokeGeo, smokeMat, MAX_FIRE_SITES * PUFFS);
    for (const [mesh, name] of [[this.flame, "fire-flames"], [this.core, "fire-cores"], [this.smoke, "fire-smoke"]] as const) {
      mesh.name = name;
      mesh.count = 0;
      mesh.frustumCulled = false;
      mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
      this.group.add(mesh);
    }
    this.alarm.name = "incoming-target-alarm";
    this.alarm.visible = false;
    const alertMat = new THREE.MeshBasicMaterial({ color: 0xf36b50, transparent: true, opacity: 0.94, depthWrite: false, toneMapped: false });
    const ringGeo = new THREE.TorusGeometry(1.32, 0.026, 5, 48);
    const arrowGeo = new THREE.ConeGeometry(0.12, 0.28, 3);
    const postGeo = new THREE.CylinderGeometry(0.018, 0.025, 1.25, 5);
    this.geometries.push(ringGeo, arrowGeo, postGeo);
    this.materials.push(alertMat);
    const ring = new THREE.Mesh(ringGeo, alertMat);
    ring.rotation.x = Math.PI / 2;
    this.alarm.add(ring);
    for (let i = 0; i < 4; i++) {
      const a = i * Math.PI / 2 + Math.PI / 4;
      const post = new THREE.Mesh(postGeo, alertMat), arrow = new THREE.Mesh(arrowGeo, alertMat);
      post.position.set(Math.cos(a) * 1.3, 0.625, Math.sin(a) * 1.3);
      arrow.position.copy(post.position); arrow.position.y = 1.5;
      arrow.rotation.z = Math.PI;
      this.alarm.add(post, arrow);
    }
    this.group.add(this.alarm);
  }

  setFires(sites: readonly FireSite[]): void {
    this.sites = sites.slice(0, MAX_FIRE_SITES).map((s) => ({ ...s }));
  }

  setIncoming(signal: IncomingSignal | null): void {
    this.incoming = signal ? { ...signal } : null;
  }

  update(now: number, reducedMotion: boolean): void {
    if (this.disposed) return;
    const phase = reducedMotion ? 0.35 : now / 1000;
    let n = 0, puff = 0, visibleSites = 0;
    for (const site of this.sites) {
      if (site.until !== null && site.until <= now) continue;
      visibleSites++;
      const seed = site.x * 0.71 + site.y * 0.39;
      for (let i = 0; i < FLAMES; i++) {
        const angle = i * Math.PI * 2 / FLAMES + seed;
        const wave = reducedMotion ? 0.6 : (Math.sin(phase * 3.1 + i * 1.8 + seed) + 1) / 2;
        this.dummy.position.set(site.x + Math.cos(angle) * 0.65, 0.38 + (i % 3) * 0.09, site.y + Math.sin(angle) * 0.65);
        this.dummy.rotation.set(Math.sin(angle) * 0.14, angle, Math.cos(angle) * 0.17);
        this.dummy.scale.set(0.18 + wave * 0.06, 0.68 + wave * 0.63, 0.18 + wave * 0.03);
        this.dummy.updateMatrix(); this.flame.setMatrixAt(n, this.dummy.matrix);
        this.dummy.scale.x *= 0.52; this.dummy.scale.y *= 0.66; this.dummy.scale.z *= 0.52;
        this.dummy.updateMatrix(); this.core.setMatrixAt(n++, this.dummy.matrix);
      }
      for (let i = 0; i < PUFFS; i++) {
        const rise = reducedMotion ? (i + 0.5) / PUFFS : ((phase * 0.16 + i / PUFFS + seed) % 1 + 1) % 1;
        this.dummy.position.set(site.x + 0.12 + rise * 0.8, 1.15 + rise * 3.4, site.y - rise * 0.28 + Math.sin(i * 2 + seed) * 0.23);
        this.dummy.rotation.set(i * 0.7, i, phase * (reducedMotion ? 0 : 0.12));
        const size = 0.15 + Math.sin(rise * Math.PI) * 0.25;
        this.dummy.scale.set(size * 1.3, size, size);
        this.dummy.updateMatrix(); this.smoke.setMatrixAt(puff++, this.dummy.matrix);
      }
    }
    this.flame.count = this.core.count = n; this.smoke.count = puff;
    this.flame.instanceMatrix.needsUpdate = this.core.instanceMatrix.needsUpdate = this.smoke.instanceMatrix.needsUpdate = true;
    this.group.userData["burningCount"] = visibleSites;
    const alarm = this.incoming;
    this.alarm.visible = !!alarm && alarm.count > 0 && alarm.arrivesAt > now;
    this.alarm.userData["count"] = this.alarm.visible ? alarm!.count : 0;
    this.alarm.userData["arrivesAt"] = alarm?.arrivesAt ?? null;
    if (this.alarm.visible && alarm) {
      this.alarm.position.set(alarm.x, 0.25, alarm.y);
      this.alarm.scale.setScalar(reducedMotion ? 1 : 1 + Math.sin(phase * 2) * 0.035);
    }
    this.group.userData["reducedMotion"] = reducedMotion;
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.flame.dispose(); this.core.dispose(); this.smoke.dispose();
    for (const g of this.geometries) g.dispose();
    for (const m of this.materials) m.dispose();
    this.group.removeFromParent(); this.group.clear();
    this.sites = []; this.incoming = null;
  }
}
